<template>
  <div class="stripe-pay-component" @focusin.once="fireFocusEvent">
    <div v-if="basket.total > 0" class="text-dark">
      <div class="custom-control custom-checkbox">
        <input v-model="nonRefundable" type="checkbox" class="custom-control-input" id="non-refundable">
        <label class="custom-control-label" for="non-refundable">I understand that once paid for,
          extras are non-refundable.</label>
      </div>

      <div class="custom-control custom-checkbox mt-2">
        <input v-model="colourVariance" type="checkbox" class="custom-control-input" id="colour-variance">
        <label class="custom-control-label" for="colour-variance">I understand that colour choices cannot be changed,
          unless the selections are no longer available from the manufacturer. We must also advise that
          tile/kitchen/worktop selections can vary in colour from the sample boards due to different batches.</label>
      </div>
    </div>

    <div v-if="!paid" class="row">
      <div class="col-12">
        <div class="totals my-3">
          <h6 v-if="basket.discount_total" class="mb-0">
            Sub Total:
            <span class="ms-2 price">&pound;{{
                (basket.sub_total + basket.eyesite_total).toFixed(2) | money
              }}</span>
          </h6>

          <h6 v-if="basket.discount_total" class="mb-0">
            Discount:
            <span class="ms-2 price">-&pound;{{ basket.discount_total | money }}</span>
          </h6>

          <h6 class="mt-3 mb-0">
            Total:
            <span class="ms-2 price">&pound;{{ basket.total | money }}</span>
          </h6>
        </div>
      </div>
    </div>

    <div v-if="!paid && basket.total > 0" class="row">
      <div class="col-12">
        <form id="payment-form" ref="payment-form">
          <div class="d-flex flex-column flex-lg-row align-items-start w-100">
            <div class="flex-grow-1 me-2 w-100 w-xl-auto">
              <div id="card-element" class="mb-3">
                <!-- Elements will create input elements here -->
              </div>

              <!-- We'll put the error messages in this element -->
              <div id="card-errors" class="alert alert-danger d-none mb-3" role="alert"></div>
            </div>

            <button class="btn btn-primary" id="submit" :disabled="loading">Submit Payment</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="paid" class="row">
      <div class="col-12">
        <div class="alert alert-success mb-0">
          This order has already been paid for.
        </div>
      </div>
    </div>

    <div v-if="basket.total <= 0" class="row">
      <div class="col-12">
        <div class="d-flex justify-content-end">
          <button @click="handleConfirmOrder" type="button" class="btn btn-success">Confirm Order</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {loadStripe} from '@stripe/stripe-js/pure';
import DiscountComponent from "./DiscountComponent";

export default {
  components: {DiscountComponent},
  data() {
    return {
      loading: false,
      basket: null,
      stripe: null,
      elements: null,
      card: null,
      nonRefundable: false,
      colourVariance: false,
    };
  },

  props: {
    preloadBasket: {
      type: Object,
      default: null,
    },
    rootBasket: {
      type: Object,
      default: null,
    },
    publishableKey: {
      type: String,
      default: null,
    },
    clientSecret: {
      type: String,
      default: null,
    },
    user: {
      type: Object,
      default: null,
    },
    paid: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: Number,
      default: null,
    },
  },

  watch: {
    rootBasket: function (newValue, oldValue) {
      if (newValue) {
        this.basket = newValue;
      }
    },
  },

  created() {
    if (this.preloadBasket) {
      this.basket = this.preloadBasket;
    }

    if (!this.paid && this.basket.total > 0) {
      this.loadStripeAsync().then(() => {
        this.elements = this.stripe.elements();

        // Styles.
        const style = {
          base: {
            color: '#212721',
            fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
            fontSize: '14px',
            // backgroundColor: '#ffffff',
            // padding: '15px 20px',
          },
        };

        // Create the card element.
        this.card = this.elements.create("card", {
          hidePostalCode: true,
          style: style
        });
        this.card.mount("#card-element");

        // Validation.
        this.card.on("change", ({error}) => {
          let displayError = document.getElementById("card-errors");
          if (error) {
            displayError.textContent = error.message;
            $('#card-errors').removeClass('d-none');
          } else {
            displayError.textContent = "";
            $('#card-errors').addClass('d-none');
          }
        });

        this.$refs['payment-form'].addEventListener("submit", (ev) => {
          ev.preventDefault();

          if (this.loading) {
            return;
          }

          if (!this.nonRefundable) {
            return notyf.error('You must confirm that you understand that once paid for, extras are non-refundable.');
          }

          if (!this.colourVariance) {
            return notyf.error('You must confirm that you understand colours cannot be changed and may vary from samples.');
          }

          this.loading = true;

          // If the client secret was rendered server-side as a data-secret attribute
          // on the <form> element, you can retrieve it here by calling `form.dataset.secret`
          this.stripe
            .confirmCardPayment(this.clientSecret, {
              payment_method: {
                card: this.card,
                billing_details: {
                  name: this.user.name,
                  email: this.user.email,
                },
              },
            })
            .then(result => {
              if (result.error) {
                // Show error to your customer (e.g., insufficient funds)
                console.log('Friendly error', result.error.message);

                notyf.error(result.error.message);

                this.loading = false;
              } else {
                // The payment has been processed!
                if (result.paymentIntent.status === "succeeded") {
                  // Show a success message to your customer
                  // There's a risk of the customer closing the window before callback
                  // execution. Set up a webhook or plugin to listen for the
                  // payment_intent.succeeded event that handles any business critical
                  // post-payment actions.
                  window.location = window.order_pending_url + '/' + this.orderId;
                }
              }
            })
            .catch(err => {
              // Show error to your customer
              console.log('Error caught', err);

              notyf.error('Something went wrong, please contact support.');

              this.loading = false;
            });
        });
      });
    }

    window.addEventListener('stripePayComponent.confirmOrderSuccess', this.confirmOrder);
  },

  beforeDestroy() {
    window.removeEventListener('stripePayComponent.confirmOrderSuccess', this.confirmOrder);
  },

  methods: {
    async loadStripeAsync() {
      this.stripe = await loadStripe(this.publishableKey);
    },

    handleConfirmOrder() {
      window.dispatchEvent(new CustomEvent('stripePayComponent.confirmingOrder'));
    },

    confirmOrder() {
      console.log('confirming order');
      this.loading = true;

      axios.post('/api/v1/basket/confirm_zero_cost_order')
        .then(response => {
          if (response.data.success) {
            window.location = response.data.order_success_url;
          } else {
            notyf.error(response.data.message);
          }
        }).catch(error => {
        this.loading = false;

        notyf.error('Something went wrong, please contact support.');
      });
    },

    fireFocusEvent() {
      // fire stripe pay component created event
      window.dispatchEvent(new CustomEvent('stripePayComponent.focused'));
    },
  },
};
</script>
